import { distance, palette, utils } from 'image-q';
const TARGET_SIZE = 64;
const PALETTE_SIZE = 5;
const MIN_PALETTE_PERCENT = 15;
/**
 * Extract a compact color palette from a base64 image.
 * Returns an empty palette if DOM APIs are unavailable or extraction fails.
 */
export async function extractPaletteFromImage(base64, mimeType) {
    if (typeof document === 'undefined' || typeof Image === 'undefined') {
        return { palette: [], backgroundColor: 'transparent' };
    }
    try {
        const img = await loadImage(base64, mimeType);
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_SIZE;
        canvas.height = TARGET_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return { palette: [], backgroundColor: 'transparent' };
        }
        ctx.clearRect(0, 0, TARGET_SIZE, TARGET_SIZE);
        ctx.drawImage(img, 0, 0, TARGET_SIZE, TARGET_SIZE);
        const imageData = ctx.getImageData(0, 0, TARGET_SIZE, TARGET_SIZE);
        return extractPaletteFromImageData(imageData.data, TARGET_SIZE, TARGET_SIZE);
    }
    catch (error) {
        console.warn('[PaletteExtraction] Failed to extract palette', error);
        return { palette: [], backgroundColor: 'transparent' };
    }
}
function extractPaletteFromImageData(data, width, height) {
    const nonTransparent = [];
    let nonTransparentCount = 0;
    for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a === 0)
            continue;
        nonTransparent.push(data[i], data[i + 1], data[i + 2], a);
        nonTransparentCount++;
    }
    const backgroundColor = getBorderBackgroundColor(data, width, height);
    if (nonTransparentCount === 0) {
        return { palette: [], backgroundColor };
    }
    let colors = [];
    try {
        const sample = new Uint8Array(nonTransparent);
        const pointContainer = utils.PointContainer.fromUint8Array(sample, nonTransparentCount, 1);
        const quantizer = new palette.WuQuant(new distance.PNGQuant(), PALETTE_SIZE);
        quantizer.sample(pointContainer);
        const quantizedPalette = quantizer.quantizeSync();
        const palettePoints = quantizedPalette.getPointContainer().getPointArray();
        colors = palettePoints.map(point => ({
            r: point.r,
            g: point.g,
            b: point.b,
        }));
    }
    catch (error) {
        console.warn('[PaletteExtraction] Quantization failed', error);
        return { palette: [], backgroundColor };
    }
    const counts = new Array(colors.length).fill(0);
    for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a === 0)
            continue;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        let bestIndex = 0;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (let c = 0; c < colors.length; c++) {
            const dr = r - colors[c].r;
            const dg = g - colors[c].g;
            const db = b - colors[c].b;
            const distance = dr * dr + dg * dg + db * db;
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = c;
            }
        }
        counts[bestIndex]++;
    }
    const paletteResult = colors.map((color, index) => ({
        hex: rgbToHex(color.r, color.g, color.b),
        percent: Math.round((counts[index] / nonTransparentCount) * 100),
    }))
        .sort((a, b) => b.percent - a.percent)
        .filter(color => color.percent > MIN_PALETTE_PERCENT);
    return { palette: paletteResult, backgroundColor };
}
function getBorderBackgroundColor(data, width, height) {
    const counts = new Map();
    const addPixel = (x, y) => {
        const index = (y * width + x) * 4;
        const r = data[index];
        const g = data[index + 1];
        const b = data[index + 2];
        const a = data[index + 3];
        const key = a === 0 ? 'transparent' : rgbToHex(r, g, b);
        counts.set(key, (counts.get(key) || 0) + 1);
    };
    for (let x = 0; x < width; x++) {
        addPixel(x, 0);
        addPixel(x, height - 1);
    }
    for (let y = 1; y < height - 1; y++) {
        addPixel(0, y);
        addPixel(width - 1, y);
    }
    let topColor = 'transparent';
    let topCount = -1;
    for (const [color, count] of counts.entries()) {
        if (count > topCount) {
            topCount = count;
            topColor = color;
        }
    }
    return topColor;
}
function rgbToHex(r, g, b) {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function toHex(value) {
    return value.toString(16).padStart(2, '0').toUpperCase();
}
function loadImage(base64, mimeType) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        const dataUrl = base64.startsWith('data:')
            ? base64
            : `data:${mimeType};base64,${base64}`;
        img.src = dataUrl;
    });
}
